import { forwardRef } from "react";

export const Label = ({ children, hint }) => (
  <div className="mb-2">
    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
      {children}
    </div>
    {hint && (
      <div className="text-[11px] text-zinc-600 mt-1 normal-case tracking-normal">
        {hint}
      </div>
    )}
  </div>
);

export const TextInput = forwardRef(function TextInput(
  { label, hint, error, testId, className = "", ...rest },
  ref
) {
  return (
    <div className={className}>
      {label && <Label hint={hint}>{label}</Label>}
      <input
        ref={ref}
        data-testid={testId}
        {...rest}
        className={`w-full bg-transparent border ${
          error ? "border-[#E60000]" : "border-white/15"
        } text-white px-4 py-3 text-base placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors`}
      />
      {error && <div className="text-[11px] text-[#E60000] mt-1">{error}</div>}
    </div>
  );
});

export function Select({ label, hint, value, onChange, options, testId, className = "" }) {
  return (
    <div className={className}>
      {label && <Label hint={hint}>{label}</Label>}
      <select
        data-testid={testId}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-base focus:outline-none focus:border-[#E60000] transition-colors appearance-none cursor-pointer"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg width='12' height='8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 8L0 0h12z' fill='%23E60000'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 16px center",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-[#0A0A0A] text-white">
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function RadioTiles({ label, hint, value, onChange, options, testId, columns = 3 }) {
  const colClass = { 1: "grid-cols-1", 2: "grid-cols-1 sm:grid-cols-2", 3: "grid-cols-1 sm:grid-cols-3" }[columns] || "grid-cols-1";
  return (
    <div>
      {label && <Label hint={hint}>{label}</Label>}
      <div className={`grid ${colClass} gap-2`} data-testid={testId}>
        {options.map((o) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              data-testid={`${testId}-${o.value}`}
              className={`text-left px-4 py-3 border transition-colors ${
                active
                  ? "border-[#E60000] bg-[#E60000]/10 text-white"
                  : "border-white/15 text-zinc-300 hover:border-white/40 hover:text-white"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold">{o.label}</span>
                <span
                  className={`w-4 h-4 border ${
                    active ? "border-[#E60000] bg-[#E60000]" : "border-white/30"
                  }`}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TextArea({ label, hint, testId, className = "", ...rest }) {
  return (
    <div className={className}>
      {label && <Label hint={hint}>{label}</Label>}
      <textarea
        data-testid={testId}
        {...rest}
        className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-base placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors resize-none"
      />
    </div>
  );
}

export function Checkbox({ label, checked, onChange, testId, id }) {
  return (
    <label
      htmlFor={id}
      className="flex items-start gap-3 cursor-pointer group"
      data-testid={`${testId}-label`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="sr-only peer"
      />
      <span
        className={`shrink-0 w-5 h-5 border ${
          checked ? "border-[#E60000] bg-[#E60000]" : "border-white/30 group-hover:border-white/60"
        } flex items-center justify-center transition-colors mt-0.5`}
      >
        {checked && (
          <svg
            viewBox="0 0 24 24"
            className="w-3.5 h-3.5 text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <span className="text-sm text-zinc-300 leading-relaxed">{label}</span>
    </label>
  );
}
